import { VIEWS } from '../hooks/useNavigation';

/**
 * Deep-link routing helpers.
 *
 * Email links can point a user straight at a piece of content, bypassing the
 * LLM chat:
 *   /quiz/:slug      -> opens the quiz fullscreen
 *   /articles/:slug  -> opens the article reader
 *   /videos/:id      -> opens the video player
 *   /community-hub   -> opens the Community Manager Hub (access-gated screen)
 *   /events          -> opens the members' Events page
 *
 * If the recipient isn't logged in, the destination is stashed in localStorage
 * (`pendingDeepLink`) before the Thinkific OAuth redirect. The whole login
 * round-trip stays in the same browser, so localStorage survives it - no server
 * or Thinkific changes are needed.
 */

// Single-segment paths the org-landing catch-all in App.jsx must NOT treat as
// an organization slug. Keep entries lowercase.
export const RESERVED_PATHS = ['landing', 'login', 'logout', 'quiz', 'articles', 'videos', 'privacy', 'tos', 'meet-sophia', 'workshop', 'circle', 'reset-password', 'register', 'community-hub', 'events'];

export const PENDING_DEEPLINK_KEY = 'pendingDeepLink';

// Pending deep links older than this are treated as stale and ignored, so an
// abandoned login attempt doesn't hijack a much later session.
const MAX_PENDING_AGE_MS = 30 * 60 * 1000; // 30 minutes

/**
 * Parse a URL pathname into a deep-link target.
 * @param {string} pathname - e.g. "/quiz/money-quiz"
 * @returns {{ view: string, data: object } | null} null if not a recognized deep link
 */
export function parseDeepLink(pathname) {
  if (!pathname || typeof pathname !== 'string') return null;

  // Defensive: strip query/hash, trailing slash, leading/empty segments.
  const cleanPath = pathname.split('?')[0].split('#')[0].replace(/\/+$/, '');
  const parts = cleanPath.split('/').filter(Boolean);

  // Single-segment screens (no identifier). Must also be in RESERVED_PATHS or
  // the org-landing catch-all in App.jsx claims the path as an org slug.
  if (parts.length === 1 && parts[0].toLowerCase() === 'community-hub') {
    return { view: VIEWS.COMMUNITY_HUB, data: {} };
  }
  if (parts.length === 1 && parts[0].toLowerCase() === 'events') {
    return { view: VIEWS.EVENTS, data: {} };
  }

  // Every other supported deep link is exactly two segments: <type>/<identifier>.
  if (parts.length !== 2) return null;

  const [segment, rawValue] = parts;
  let value;
  try {
    value = decodeURIComponent(rawValue).trim();
  } catch (e) {
    value = rawValue.trim();
  }
  if (!value) return null;

  switch (segment.toLowerCase()) {
    case 'quiz':
      return { view: VIEWS.QUIZ_DETAIL, data: { slug: value } };
    case 'articles':
      return { view: VIEWS.ARTICLE_DETAIL, data: { slug: value } };
    case 'videos':
      // `value` is the Strapi `videoId` field (slug-like, e.g. "what-is-coaching").
      return { view: VIEWS.VIDEO_DETAIL, data: { videoId: value } };
    case 'circle':
      // Circle invite link: /circle/{invite_token}. The Circles screen shows a
      // join preview and calls accept. Tokens are reusable until expiry, so a
      // brand-new user whose deep link is discarded by the onboarding gate can
      // simply tap the same link again afterwards.
      return { view: VIEWS.CIRCLES, data: { inviteToken: value } };
    default:
      return null;
  }
}

/** Persist a deep-link path so it survives a login round-trip (same browser). */
export function savePendingDeepLink(pathname) {
  try {
    localStorage.setItem(
      PENDING_DEEPLINK_KEY,
      JSON.stringify({ path: pathname, ts: Date.now() })
    );
  } catch (e) {
    /* localStorage unavailable - deep link simply won't survive login */
  }
}

/**
 * Read and remove the pending deep-link path.
 * @returns {string | null} the stored pathname, or null if absent/stale/malformed
 */
export function consumePendingDeepLink() {
  try {
    const raw = localStorage.getItem(PENDING_DEEPLINK_KEY);
    if (!raw) return null;
    localStorage.removeItem(PENDING_DEEPLINK_KEY);

    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.path) return null;
    if (typeof parsed.ts === 'number' && Date.now() - parsed.ts > MAX_PENDING_AGE_MS) {
      return null; // stale link from an abandoned login
    }
    return parsed.path;
  } catch (e) {
    return null;
  }
}
